import { message } from 'ant-design-vue'
import request, { IPage, IWorkspaceResponse, IListWorkspaceResponse, IWorkspaceResponseData } from '/@/api/http/request'
import { TaskType, TaskStatus, OutOfControlAction } from '/@/types/task'
import { WaylineType } from '/@/types/wayline'

const HTTP_PREFIX = '/wayline/api/v1'

// Get Wayline Files
export const getWaylineFiles = async function (wid: string, body: {}): Promise<IWorkspaceResponse<any>> {
  let url = `${HTTP_PREFIX}/workspaces/${wid}/waylines?order_by=${body.order_by}&page=${body.page}&page_size=${body.page_size}`
  // 添加搜索参数 key
  if (body.key) {
    url += `&key=${encodeURIComponent(body.key)}`
  }
  const result = await request.get(url)
  return result.data
}

// Download Wayline File
export const downloadWaylineFile = async function (workspaceId: string, waylineId: string): Promise<any> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/${waylineId}/url`
  const result = await request.get(url, { responseType: 'blob' })
  if (result.data.type === 'application/json') {
    const reader = new FileReader()
    reader.onload = function (e) {
      const text = reader.result as string
      const result = JSON.parse(text)
      message.error(result.message)
    }
    reader.readAsText(result.data, 'utf-8')
  } else {
    return result.data
  }
}

// Analysis Wayline File
export const analysisWaylineFile = async function (workspaceId: string, waylineId: string): Promise<any> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/${waylineId}/analysis`
  const result = await request.get(url)
  return result.data
}

// Delete Wayline File
export const deleteWaylineFile = async function (workspaceId: string, waylineId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/${waylineId}`
  const result = await request.delete(url)
  return result.data
}

export interface CreatePlan {
  name: string,
  file_id: string,
  dock_sn: string,
  landing_dock_sn: string,
  task_type: TaskType, // 任务类型
  wayline_type: WaylineType, // 航线类型
  dom?: number[], // 执行任务的日
  task_days: number[], // 执行任务的日期（秒）
  task_periods: number[][], // 执行任务的时间点（秒）
  rth_altitude: number, // 相对机场返航高度 20 - 500
  out_of_control_action: OutOfControlAction, // 失控动作
  min_battery_capacity?: number, // The minimum battery capacity of aircraft.
  min_storage_capacity?: number, // The minimum storage capacity of dock and aircraft.
  break_point: string,
  is_enable?: number,
  longitude?: number,
  latitude?: number,
  wayline_precision_type?: number,
}

// Create Wayline Job
export const createPlan = async function (workspaceId: string, plan: CreatePlan): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/flight-tasks`
  const result = await request.post(url, plan)
  return result.data
}

// Create Wayline Job
export const taskFlight = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = 'jeecg-boot/api/flytask/ApiUpdateApprove'
  const result = await request.post(url, body)
  return result.data
}

export interface Task {
  job_id: string,
  track_id: string,
  job_name: string,
  task_type: TaskType, // 任务类型
  file_id: string, // 航线文件id
  file_name: string, // 航线名称
  wayline_type: WaylineType, // 航线类型
  dock_sn: string,
  landing_dock_sn: string,
  dock_name: string,
  workspace_id: string,
  username: string,
  begin_time: string,
  end_time: string,
  execute_time: string,
  completed_time: string,
  status: TaskStatus, // 任务状态
  progress: number, // 执行进度
  code: number, // 错误码
  rth_altitude: number // 相对机场返航高度 20 - 500
  out_of_control_action: OutOfControlAction // 失控动作
  media_count: number // 媒体数量
  uploading:boolean // 是否正在上传媒体
  uploaded_count: number // 已上传媒体数量
  break_point: string // 断点续飞信息
  device_name: string
  device_sn: string
  air_traffic_approval_status: string // 运管审批状态
}

// Get Wayline Jobs
export const getWaylineJobs = async function (workspaceId: string, dockSn: string, page: IPage, filters?: {
  status?: number,
  type?: number,
  job_name?: string,
  wayline_name?: string,
  device_name?: string,
  begin_time_start?: string,
  begin_time_end?: string
}): Promise<IListWorkspaceResponse<Task>> {
  let url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs?dock_sn=${dockSn}&page=${page.page}&page_size=${page.page_size}`

  // 添加筛选参数
  if (filters) {
    if (filters.status !== undefined) {
      url += `&status=${filters.status}`
    }
    if (filters.type !== undefined) {
      url += `&type=${filters.type}`
    }
    if (filters.job_name) {
      url += `&job_name=${encodeURIComponent(filters.job_name)}`
    }
    if (filters.wayline_name) {
      url += `&wayline_name=${encodeURIComponent(filters.wayline_name)}`
    }
    if (filters.device_name) {
      url += `&device_name=${encodeURIComponent(filters.device_name)}`
    }
    if (filters.begin_time_start) {
      url += `&begin_time_start=${encodeURIComponent(filters.begin_time_start)}`
    }
    if (filters.begin_time_end) {
      url += `&begin_time_end=${encodeURIComponent(filters.begin_time_end)}`
    }
  }

  const result = await request.get(url)
  return result.data
}

// Get Wayline JobsLists
export const getWaylineJobsLists = async function (workspaceId: string, fileId: string, dockSn: string): Promise<IWorkspaceResponseData<Task>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobslists?file_id=${fileId}&dock_sn=${dockSn}`
  const result = await request.get(url)
  return result.data
}

export interface DeleteTaskParams {
  job_id: string | string[]
}

//  删除机场任务（支持批量删除）
export async function deleteTask (workspaceId: string, params: DeleteTaskParams): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs`

  // 处理单个和批量删除
  const jobIds = Array.isArray(params.job_id) ? params.job_id : [params.job_id]

  // 构建查询参数，格式为：job_id=value1&job_id=value2&job_id=value3
  const queryParams = new URLSearchParams()
  jobIds.forEach(jobId => {
    queryParams.append('job_id', jobId)
  })

  const result = await request.delete(`${url}?${queryParams.toString()}`)
  return result.data
}

export enum UpdateTaskStatus {
  Suspend = 0, // 暂停
  Resume = 1, // 恢复
}
export interface UpdateTaskStatusBody {
  job_id: string
  status: UpdateTaskStatus
}

// 更新机场任务状态
export async function updateTaskStatus (workspaceId: string, body: UpdateTaskStatusBody): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${body.job_id}`
  const result = await request.put(url, {
    status: body.status
  })
  return result.data
}

// Upload Wayline file
export const importKmzFile = async function (workspaceId: string, file: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/file/upload`
  const result = await request.post(url, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  })
  return result.data
}

export const updateKmzFile = async function (workspaceId: string, file: {}, waylineId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/file/upload/${waylineId}`
  const result = await request.post(url, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  })
  return result.data
}

// 媒体立即上传
export const uploadMediaFileNow = async function (workspaceId: string, jobId: string): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${jobId}/media-highest`
  const result = await request.post(url)
  return result.data
}

// Create Wayline
export const createWayline = async function (workspaceId: string, body: any): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/add`
  const result = await request.post(url, body)
  return result.data
}

// Update Wayline
export const updateWayline = async function (workspaceId: string, body: any): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/update`
  const result = await request.post(url, body)
  return result.data
}

// Create Wayline
export const getWaylineById = async function (workspaceId: string, waylineId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/detail/${waylineId}`
  const result = await request.get(url)
  return result.data
}

// get Job
export const getJobById = async function (workspaceId: string, jobId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${jobId}`
  const result = await request.get(url)
  return result.data
}

// get JobStatus
export const getJobStatus = async function (workspaceId: string, dockSn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobStatus/${dockSn}`
  const result = await request.get(url)
  return result.data
}
